/**
 * AI Assistant Settings Panel
 * 
 * Allows users to configure AI Assistant features:
 * - Proactive Suggestions
 * - Element Explanations
 * - Auto-Generate Configurations
 */

import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useAIAssistant } from './AIAssistantContext';

// ============================================
// ANIMATIONS
// ============================================

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const SettingsContainer = styled.div`
  animation: ${slideIn} 0.2s ease-out;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  flex: 1;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 2px;
  }
`;

const SettingsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  
  h3 {
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const BackButton = styled.button`
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 6px;
  color: #a5b4fc;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.2);
    border-color: rgba(99, 102, 241, 0.4);
  }
`;

const SettingsSection = styled.div`
  background: rgba(15, 20, 35, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.1);
  border-radius: 10px;
  padding: 14px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  
  h4 {
    font-size: 13px;
    font-weight: 600;
    color: #e0e7ff;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  span {
    font-size: 11px;
    color: rgba(165, 180, 252, 0.6);
  }
`;

const SectionDescription = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 12px 0;
  line-height: 1.5;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  &:first-child {
    padding-top: 0;
  }
`;

const ToggleLabel = styled.label`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
`;

const Toggle = styled.button`
  width: 40px;
  height: 22px;
  border-radius: 11px;
  border: none;
  background: ${props => props.active ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : 'rgba(255, 255, 255, 0.1)'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.active ? '20px' : '2px'};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }
  
  &:hover {
    background: ${props => props.active ? 'linear-gradient(135deg, #7c7ff2 0%, #9d7ff7 100%)' : 'rgba(255, 255, 255, 0.15)'};
  }
`;

const SelectRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
`;

const SelectLabel = styled.label`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
`;

const Select = styled.select`
  background: rgba(25, 30, 50, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 6px;
  color: #fff;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }
  
  option {
    background: #1a1f35;
  }
`;

const SubSettings = styled.div`
  margin-top: 10px;
  padding: 10px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  
  ${props => !props.enabled && `
    opacity: 0.5;
    pointer-events: none;
  `}
`;

const SubSettingsTitle = styled.div`
  font-size: 11px;
  color: rgba(165, 180, 252, 0.7);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// ============================================
// ICONS
// ============================================

const Icons = {
  settings: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  lightbulb: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6M10 22h4M12 2v1M4.22 4.22l.71.71M1 12h2M4.22 19.78l.71-.71M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10z"/></svg>,
  info: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
  magic: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z"/></svg>,
};

// ============================================
// COMPONENT
// ============================================

const AIAssistantSettings = () => {
  const { settings, updateSettings, toggleSettingsPanel } = useAIAssistant();

  const handleToggle = (key, subKey = null) => {
    if (subKey) {
      updateSettings({
        [key]: {
          ...settings[key],
          [subKey]: !settings[key][subKey]
        }
      });
    } else {
      updateSettings({ [key]: !settings[key] });
    }
  };

  const handleSelect = (key, value) => {
    updateSettings({ [key]: value });
  };

  return (
    <SettingsContainer>
      <SettingsHeader>
        <h3>{Icons.settings} Assistant Settings</h3>
        <BackButton onClick={toggleSettingsPanel}>← Back to Chat</BackButton>
      </SettingsHeader>

      {/* Proactive Suggestions */}
      <SettingsSection>
        <SectionHeader>
          <h4>{Icons.lightbulb} Proactive Suggestions</h4>
          <Toggle 
            active={settings.proactiveSuggestions}
            onClick={() => handleToggle('proactiveSuggestions')}
          />
        </SectionHeader>
        <SectionDescription>
          Get automatic suggestions based on what you're doing, like next steps and helpful tips.
        </SectionDescription>
        
        <SubSettings enabled={settings.proactiveSuggestions}>
          <SelectRow>
            <SelectLabel>Frequency</SelectLabel>
            <Select 
              value={settings.suggestionFrequency}
              onChange={(e) => handleSelect('suggestionFrequency', e.target.value)}
            >
              <option value="always">Always</option>
              <option value="sometimes">Sometimes</option>
              <option value="minimal">Minimal (warnings only)</option>
            </Select>
          </SelectRow>
          
          <SubSettingsTitle>Suggestion Types</SubSettingsTitle>
          <ToggleRow>
            <ToggleLabel>Next step suggestions</ToggleLabel>
            <Toggle 
              active={settings.suggestionTypes?.nextSteps}
              onClick={() => handleToggle('suggestionTypes', 'nextSteps')}
            />
          </ToggleRow>
          <ToggleRow>
            <ToggleLabel>Helpful tips</ToggleLabel>
            <Toggle 
              active={settings.suggestionTypes?.tips}
              onClick={() => handleToggle('suggestionTypes', 'tips')}
            />
          </ToggleRow>
          <ToggleRow>
            <ToggleLabel>Warnings about issues</ToggleLabel>
            <Toggle 
              active={settings.suggestionTypes?.warnings}
              onClick={() => handleToggle('suggestionTypes', 'warnings')}
            />
          </ToggleRow>
        </SubSettings>
      </SettingsSection>

      {/* Element Explanations */}
      <SettingsSection>
        <SectionHeader>
          <h4>{Icons.info} Element Explanations</h4>
          <Toggle 
            active={settings.elementExplanations}
            onClick={() => handleToggle('elementExplanations')}
          />
        </SectionHeader>
        <SectionDescription>
          See explanations when you hover over UI elements to understand what they do.
        </SectionDescription>
        
        <SubSettings enabled={settings.elementExplanations}>
          <SelectRow>
            <SelectLabel>Detail level</SelectLabel>
            <Select 
              value={settings.explanationDetail}
              onChange={(e) => handleSelect('explanationDetail', e.target.value)}
            >
              <option value="brief">Brief</option>
              <option value="detailed">Detailed</option>
            </Select>
          </SelectRow>
          
          <ToggleRow>
            <ToggleLabel>Show keyboard shortcuts</ToggleLabel>
            <Toggle 
              active={settings.showShortcuts}
              onClick={() => handleToggle('showShortcuts')}
            />
          </ToggleRow>
        </SubSettings>
      </SettingsSection>

      {/* Auto-Generate */}
      <SettingsSection>
        <SectionHeader>
          <h4>{Icons.magic} Auto-Generate</h4>
          <Toggle 
            active={settings.autoGenerateEnabled}
            onClick={() => handleToggle('autoGenerateEnabled')}
          />
        </SectionHeader>
        <SectionDescription>
          Let the AI generate flows, rules, and directives from natural language descriptions.
        </SectionDescription>
        
        <SubSettings enabled={settings.autoGenerateEnabled}>
          <SubSettingsTitle>Enable generation for</SubSettingsTitle>
          <ToggleRow>
            <ToggleLabel>Scenario flows</ToggleLabel>
            <Toggle 
              active={settings.autoGenerateTypes?.flows}
              onClick={() => handleToggle('autoGenerateTypes', 'flows')}
            />
          </ToggleRow>
          <ToggleRow>
            <ToggleLabel>Rule sets</ToggleLabel>
            <Toggle 
              active={settings.autoGenerateTypes?.rules}
              onClick={() => handleToggle('autoGenerateTypes', 'rules')}
            />
          </ToggleRow>
          <ToggleRow>
            <ToggleLabel>Directives</ToggleLabel>
            <Toggle 
              active={settings.autoGenerateTypes?.directives}
              onClick={() => handleToggle('autoGenerateTypes', 'directives')}
            />
          </ToggleRow>
          
          <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
            <ToggleRow>
              <ToggleLabel>Confirm before generating</ToggleLabel>
              <Toggle 
                active={settings.confirmBeforeGenerate}
                onClick={() => handleToggle('confirmBeforeGenerate')}
              />
            </ToggleRow>
          </div>
        </SubSettings>
      </SettingsSection>
    </SettingsContainer>
  );
};

export default AIAssistantSettings;


